// 🔧 /pages/admin/users.js
// ✅ Purpose: Manager/admin page to view and manage all users for the current event
// ✅ Features:
// - Uses the standard Let's Go Vote responsive manager page layout
// - Loads event-scoped users only
// - Loads matching global user profiles without scanning all users
// - Shows summary cards for total users, voters, managers/admins, votes, and entries
// - Displays users as responsive cards instead of a dense table
// - Shows role, vote count, and entry/item count badges
// - Links each user to user_profile edit page
// - Supports email mailto links and phone display
// - Exports event users to XLSX
// - Keeps pagination for large events
// - Shows success/error notifications for 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useRole } from "../../role_context";
import * as XLSX from "xlsx";

import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { useEventContext } from "../../hooks/use_event_context";

// =====================================================
// 🧩 Fetch global user profiles only for event users
// =====================================================
// Firestore "in" queries have a limited list size, so we chunk user IDs.
// This prevents loading the entire global /users collection.

const fetchGlobalUsersByIds = async (userIds = []) => {

  if (!userIds.length) return [];

  const chunkSize = 10;
  const chunks = [];

  for (let i = 0; i < userIds.length; i += chunkSize) {
    chunks.push(userIds.slice(i, i + chunkSize));
  }

  const results = [];

  for (const chunk of chunks) {
    const usersQuery = query(
      collection(db, "users"),
      where(documentId(), "in", chunk)
    );

    const usersSnap = await getDocs(usersQuery);

    usersSnap.forEach((docSnap) => {
      results.push({
        id: docSnap.id,
        ...docSnap.data(),
      });
    });
  }

  return results;
};

const AdminUsers = () => {
  // 🔒 Role context
  const { role } = useRole();

  // 🧭 Router helper
  const navigate = useNavigate();

  // 🌎 Event context
  const {
    eventslug,
    eventId,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  // 🧠 Page state
  const [users, setUsers] = useState([]);
  const [cars, setCars] = useState([]);
  const [votes, setVotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState(null);

  // 📄 Pagination setting
  const usersPerPage = 24;

  logger.log("🟢 users.js loaded", {});
  logger.log("🧭 useRole:", {
    value: role,
  });

  // =====================================================
  // 📦 Load event users, matching global profiles, cars, and votes
  // =====================================================
  useEffect(() => {
    const loadAll = async () => {
      if (!eventId) {
        logger.warn("⚠️ Skipping users load — eventId is not ready yet.", {});
        return;
      }

      logger.log("📥 Loading event-scoped users page data:", {
        value: eventId,
      });

      setLoading(true);

      try {
        // =====================================================
        // 👥 Load only users attached to this event
        // =====================================================
        const eventUsersSnap = await getDocs(
          collection(db, "events", eventId, "users")
        );

        const eventUsers = eventUsersSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const eventUserIds = eventUsers.map((u) => u.id).filter(Boolean);

        // =====================================================
        // 🌎 Load only matching global user profiles
        // =====================================================
        const globalUsers = await fetchGlobalUsersByIds(eventUserIds);

        // =====================================================
        // 🚗 Load event-scoped entries/items
        // =====================================================
        const eventCarsSnap = await getDocs(
          collection(db, "events", eventId, "cars")
        );

        const eventCars = eventCarsSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        // =====================================================
        // 🗳️ Load event-scoped votes
        // =====================================================
        const eventVotesSnap = await getDocs(
          collection(db, "events", eventId, "votes")
        );

        const eventVotes = eventVotesSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        logger.log("✅ Event users page data loaded:", {
          eventUsers: eventUsers.length,
          globalUsers: globalUsers.length,
          entries: eventCars.length,
          votes: eventVotes.length,
        });

        // =====================================================
        // 🧩 Build profile map
        // =====================================================
        const profileMap = {};

        globalUsers.forEach((u) => {
          profileMap[u.id] = u;
        });

        // =====================================================
        // 🧑 Merge event role data with global profile data
        // =====================================================
        const enrichedUsers = eventUsers
          .map((eventUser) => {
            const globalProfile = profileMap[eventUser.id] || {};

            return {
              id: eventUser.id,
              ...globalProfile,
              ...eventUser,
              role: eventUser.role || globalProfile.role || "voter",
              eventId,
            };
          })
          .sort(
            (a, b) =>
              (a.lastName || "").localeCompare(b.lastName || "") ||
              (a.firstName || "").localeCompare(b.firstName || "")
          );

        setUsers(enrichedUsers);
        setCars(eventCars);
        setVotes(eventVotes);
        setCurrentPage(1);
      } catch (err) {
        logger.error("❌ Failed to load users page data:", {
          value: err,
        });

        setStatus({
          type: "danger",
          message: "Failed to load users for this event.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [eventId]);

  // ⏱️ Auto-clear success/error messages after 20 seconds
  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  // 🧮 Count entries/items owned by user
  const countUserItems = (uid) => {
    return cars.filter((c) => c.ownerId === uid).length;
  };

  // 🧮 Count votes submitted by user
  const countUserVotes = (uid) => {
    return votes.filter((v) => v.uid === uid).length;
  };

  // 🧑 Build display name safely
  const getDisplayName = (user) => {
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();

    return fullName || user.email || "Unnamed User";
  };

  // 🏷️ Role badge style helper
  const getRoleBadgeClass = (userRole) => {
    const cleanRole = String(userRole || "").toLowerCase();

    if (cleanRole === "admin") return "bg-danger";
    if (cleanRole === "manager") return "bg-warning text-dark";
    if (cleanRole === "volunteer") return "bg-info text-dark";

    return "bg-secondary";
  };

  // 📤 Export user data to XLSX
  const exportToXLSX = () => {
    const rows = users.map((u) => ({
      FirstName: u.firstName || "",
      LastName: u.lastName || "",
      Email: u.email || "",
      Phone: u.phone || "",
      Role: u.role || "voter",
      Votes: countUserVotes(u.id),
      Items: countUserItems(u.id),
      UserId: u.id,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Users");
    XLSX.writeFile(wb, "Event_Users.xlsx");

    setStatus({
      type: "success",
      message: "User export created successfully.",
    });
  };

  // 📄 Pagination calculations
  const indexOfLast = currentPage * usersPerPage;
  const indexOfFirst = indexOfLast - usersPerPage;
  const currentUsers = users.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(users.length / usersPerPage);

  // 📄 Pagination handler
  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 📊 Summary card counts
  const totalUsers = users.length;
  const totalVoters = users.filter(
    (u) => String(u.role || "voter").toLowerCase() === "voter"
  ).length;
  const totalVotes = votes.length;
  const totalItems = cars.length;

  // ⏳ Loading state
  if (!ready || loading || eventLoading) {
    return <FullPageSpinner message="Loading Users..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1400px" }}>
        {/* ✅ Page Header */}
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">Event Users</h1>
            <p className="lead text-muted mb-0">
              Manage voters, managers, admins, and event participants for this
              event.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/${eventslug}/user_profile/new`)}
            >
              ➕ Add Voter
            </button>
          </div>
        </div>

        {/* ✅ Status Message */}
        {status && (
          <div className={`alert alert-${status.type} mb-4`} role="alert">
            {status.message}
          </div>
        )}

        {/* ✅ Summary Cards */}
        <div className="row g-4 mb-4">
          <div className="col-12 col-md-6 col-xl">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Total Users</div>
                <div className="fs-3 fw-bold">{totalUsers}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Voters</div>
                <div className="fs-3 fw-bold">{totalVoters}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Votes</div>
                <div className="fs-3 fw-bold">{totalVotes}</div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl">
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="text-muted small">Entries</div>
                <div className="fs-3 fw-bold">{totalItems}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Tools Card */}
        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>User Tools</strong>
          </div>

          <div className="card-body">
            <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
              <div className="text-muted">
                Showing{" "}
                <strong>
                  {users.length === 0 ? 0 : indexOfFirst + 1}
                </strong>{" "}
                to <strong>{Math.min(indexOfLast, users.length)}</strong> of{" "}
                <strong>{users.length}</strong> users.
              </div>

              <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                <button
                  className="btn btn-outline-success"
                  onClick={exportToXLSX}
                >
                  📤 Export Users
                </button>

                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/${eventslug}/user_profile/new`)}
                >
                  ➕ Add Voter
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ User Cards */}
        {users.length > 0 ? (
          <div className="row g-4">
            {currentUsers.map((u) => {
              const userVoteCount = countUserVotes(u.id);
              const userItemCount = countUserItems(u.id);

              return (
                <div key={u.id} className="col-12 col-md-6 col-xl-4">
                  <div className="card h-100 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                        <div>
                          <h5 className="mb-1">{getDisplayName(u)}</h5>

                          {u.email ? (
                            <a
                              href={`mailto:${u.email}`}
                              className="small text-decoration-none"
                            >
                              {u.email}
                            </a>
                          ) : (
                            <div className="small text-muted">No email</div>
                          )}
                        </div>

                        <span
                          className={`badge ${getRoleBadgeClass(u.role)}`}
                        >
                          {u.role || "voter"}
                        </span>
                      </div>

                      <div className="mb-3">
                        <div className="small text-muted">Phone</div>
                        <div>{u.phone || "—"}</div>
                      </div>

                      <div className="d-flex flex-wrap gap-2 mb-3">
                        <span className="badge bg-primary">
                          {userVoteCount} vote
                          {userVoteCount !== 1 ? "s" : ""}
                        </span>

                        <span className="badge bg-secondary">
                          {userItemCount} entr
                          {userItemCount === 1 ? "y" : "ies"}
                        </span>
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        <Link
                          className="btn btn-sm btn-outline-secondary"
                          to={`/${eventslug}/user_profile/${u.id}`}
                        >
                          ✏️ Edit User
                        </Link>

                        {u.email && (
                          <a
                            className="btn btn-sm btn-outline-primary"
                            href={`mailto:${u.email}`}
                          >
                            ✉️ Email
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card shadow-sm">
            <div className="card-body text-center py-5">
              <h4>No users found</h4>
              <p className="text-muted">
                Add a voter or invite users to begin managing event
                participation.
              </p>

              <button
                className="btn btn-primary"
                onClick={() => navigate(`/${eventslug}/user_profile/new`)}
              >
                ➕ Add Voter
              </button>
            </div>
          </div>
        )}

        {/* ✅ Pagination */}
        {users.length > usersPerPage && (
          <div className="card shadow-sm mt-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <button
                  className="btn btn-outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  ⬅ Prev
                </button>

                <div className="d-flex align-items-center gap-2">
                  <span>Page</span>

                  <select
                    className="form-select"
                    style={{ width: "auto" }}
                    value={currentPage}
                    onChange={(e) =>
                      handlePageChange(Number(e.target.value))
                    }
                  >
                    {Array.from(
                      { length: totalPages },
                      (_, i) => i + 1
                    ).map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>

                  <span>of {totalPages}</span>
                </div>

                <button
                  className="btn btn-outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next ➡
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminUsers;